import { ActionTree, Module } from "vuex";
import { RootState } from "../store";
import { pulsingDot } from "../../../../utils/helpers/pulsing_point_mapbox";
import graph_client from "../../../../utils/graphql/graph_client";
import { GetLiveLocatorSessionByObfuscatedId } from "../../../../utils/graphql/live-locator-graph";
import { MeasurementPrefUtility } from "../../../../utils/helpers/MeasurementPref";
import { database, ref, off, onValue} from '../../../../utils/firebase/database'
import mapboxgl from "mapbox-gl";

export class LiveLocatorState {
  miniCoordsBar: string = ''
  geoJsonFeature: any = null
  liveLocatorData: any = null
  distance: string = ''
  elevationGain: string = ''
  loading: boolean = false
  initialBoundsSet: boolean = false

  dataLoaded: boolean = false
}

let listeners: any[] = []

const actions: ActionTree<LiveLocatorState, RootState> = {
  watchFirebase(store) {
    store.state.loading = false
    const databaseReference = ref(database, 'live_locator_sessions/' + window.obfuscatedId)
    listeners.forEach((listener) => {
      off(databaseReference, 'value', listener)
    })
    listeners.push(
      onValue(databaseReference, (snapshot) => {
        store.state.dataLoaded = true
        if (snapshot.exists()) {
          let geoJson = snapshot.val()
          if (geoJson != null && geoJson.geometry != null && geoJson.geometry.coordinates.length > 1) {
            store.state.geoJsonFeature = geoJson
            store.dispatch('lineStringUpdated')
            console.log("Live locator data")
          } else {
            console.log('No data found')
          }
        } else {
          if (store.state.liveLocatorData) {
            store.state.liveLocatorData.endTime = new Date().toISOString()
          }
          console.log('No data found')
        }
      })
    )
  },
  lineStringUpdated(store) {
    const geoJson = store.state.geoJsonFeature
    let measurementPref = MeasurementPrefUtility.preference()
    store.state.elevationGain = measurementPref.smallUnitFormatter(
      geoJson.properties.elevation_gain_meters, false
    )
    store.state.distance = measurementPref.smallUnitFormatter(
      geoJson.properties.distance_meters, true
    )

    let geoJsonSource: mapboxgl.GeoJSONSource = {
      'type': 'geojson',
      // @ts-ignore
      'data': geoJson
    }

    // @ts-ignore
    const lastPoint = geoJson.geometry.coordinates[geoJson.geometry.coordinates.length - 1]
    if (!store.state.initialBoundsSet) {
      window.map.flyTo({ center: lastPoint, zoom: 17 })
    }
    store.state.initialBoundsSet = true

    if (window.map.getLayer('line')) {
      // @ts-ignore
      window.map.getSource('line').setData(geoJsonSource.data)
      if (window.map.getSource('dot-point')) {
        // @ts-ignore
        window.map.getSource('dot-point').setData({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: lastPoint,
          },
          properties: {},
        })
      } else {
        throw Error('dot-point source not found')
      }
    } else {
      window.map.addSource('line', geoJsonSource)
      window.map.addLayer({
        id: 'line',
        type: "line",
        source: 'line',
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#DB2B35",
          "line-width": 5,
        }
      });
      window.map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });

      window.map.addSource('dot-point', {
        'type': 'geojson',
        'data': {
          'type': 'FeatureCollection',
          'features': [
            {
              'type': 'Feature',
              'geometry': {
                'type': 'Point',
                'coordinates': lastPoint
              },
              'properties': {}
            }
          ]
        }
      });
      window.map.addLayer({
        'id': 'layer-with-pulsing-dot',
        'type': 'symbol',
        'source': 'dot-point',
        'layout': {
          'icon-image': 'pulsing-dot'
        }
      });
    }
  },
  async getLiveLocatorData(store) {
    try {
      store.state.liveLocatorData = (await graph_client.request(
        GetLiveLocatorSessionByObfuscatedId, { obfuscatedId: window.obfuscatedId }
      )).liveLocatorSessionByObfuscatedId
    } catch (e) {
      alert('Error getting live locator data: ' + e)
    }
  }
}

const mutations = {
  setMiniCoordsBar(state: LiveLocatorState, miniCoordsBar: string) {
    state.miniCoordsBar = miniCoordsBar
  }
}

const getters = {
  mapOptions(state: LiveLocatorState): object {
    return {
      container: 'map', // container ID
      zoom: 5, // starting zoom
      maxPitch: 85, // limit the pitch to 85 degrees
    }
  }
}

const state = new LiveLocatorState();

export default class LiveLocatorModule implements Module<LiveLocatorState, RootState> {
  namespaced = true
  state = state
  actions = actions
  mutations = mutations
  getters = getters
}
